import { Button, Col, Form, Row } from 'react-bootstrap';
import { Dayts, DaytsFormatEnum } from '@/utils';
import { convertDtrLocationToPotentialLocation, getHistory } from '../../api/services';
import { useCallback, useEffect, useRef } from 'react';

import DateInput from '@/components/DateInput';
import Dropdown from '@/components/Dropdown';
import LocationDropdown from '@/features/DriveTimeTool/components/Controls/LocationDropdown';
import { createPersistentComponentState } from '@/state';
import { customSearchStorage } from '@/state/storage';
import { formatFullAddress } from '../../utils/locations';
import { useDriveTimeTool } from '@/features/DriveTimeTool';
import usePopState from '@/hooks/usePopState';

export type ReportHistoryFiltersState = {
  startDate: string;
  endDate: string;
  puLocationId: string;
  doLocationId: string;
  type: 'report' | 'import';
  importId?: string;
};
export const initReportHistoryFiltersState: ReportHistoryFiltersState = {
  startDate: new Dayts().startOf('month').format(DaytsFormatEnum.DATE_INPUT), // component should be using date input format
  endDate: new Dayts().endOf('day').format(DaytsFormatEnum.DATE_INPUT),
  puLocationId: '',
  doLocationId: '',
  type: undefined,
  importId: '',
};

export const useReportHistoryFilters = createPersistentComponentState<ReportHistoryFiltersState>(
  'useReportHistoryFilters',
  initReportHistoryFiltersState,
  {
    live: false,
    storage: customSearchStorage,
    reviver: (key: string, value: any) => {
      switch (key) {
        case 'type': {
          const reportHistoryTypeEnumKeys = ['report', 'import'];
          return reportHistoryTypeEnumKeys.includes(value) ? value : '';
        }
        default: {
          return value;
        }
      }
    },
  }
);
const HistoryFilters = () => {
  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);
  const [filters, setState, saveState, rehydrateState] = useReportHistoryFilters(({ state, setState, saveState, rehydrate }) => [
    state,
    setState,
    saveState,
    rehydrate,
  ]);
  const onFilterChange = (field: keyof ReportHistoryFiltersState, value: string | Date) => {
    setState((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const onSubmit = async () => {
    useDriveTimeTool.getState().setState((current) => ({ ...current, loading: { ...current.loading, history: true } }));
    const filters = useReportHistoryFilters.getState().state;
    // get grouped location ids by address to search
    const state = useDriveTimeTool.getState().state;
    const puLocationIds = state.groupedLocationIdsByAddress.get(
      formatFullAddress(convertDtrLocationToPotentialLocation(state.locations.get(filters.puLocationId)))
    );
    const doLocationIds = state.groupedLocationIdsByAddress.get(
      formatFullAddress(convertDtrLocationToPotentialLocation(state.locations.get(filters.doLocationId)))
    );
    const res = await getHistory({ ...filters, puLocationId: puLocationIds, doLocationId: doLocationIds });
    useDriveTimeTool.getState().setState((current) => ({ ...current, history: res, loading: { ...current.loading, history: false } }));
  };

  const handleSubmit = () => {
    onSubmit().then(saveState);
  };

  const handleReset = useCallback((): void => {
    const startDate = new Dayts().startOf('month').dateInput;
    const endDate = new Dayts().endOf('day').dateInput;
    setState({ ...initReportHistoryFiltersState, startDate, endDate });
    onSubmit();
    saveState();
  }, [setState, saveState, onSubmit]);

  const handlePopState = useCallback(() => rehydrateState().then(handleSubmit), [rehydrateState, handleSubmit]);
  usePopState(handlePopState);

  useEffect(() => {
    handlePopState().then(() => {
      if (!useReportHistoryFilters.getState().state.importId) return;
      // if we have an import id, we want to scroll to the report history section
      document.querySelector('.DriveTimeTool-History').scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  return (
    <div className="HistoryFilters rounded-3 p-3 shadow-box">
      <Row className="align-items-center g-3">
        <Col xs="auto">
          <Form.Label className="filter-label mb-0">Date Range:</Form.Label>
        </Col>
        <Col xs="auto">
          <DateInput.New
            placeholder="mm/dd/yyyy"
            className="date-input shadow-box"
            value={[filters.startDate]}
            onChange={(start) => {
              const val = new Dayts(start).startOf('day');
              const end = filters.endDate ? new Dayts(filters.endDate).endOf('day') : false;
              onFilterChange('startDate', val.format(DaytsFormatEnum.DATE_INPUT));
              if (!end || val.isAfter(end)) onFilterChange('endDate', new Dayts(val).endOf('day').format(DaytsFormatEnum.DATE_INPUT));
              startDateRef.current?.click();
            }}
            ref={startDateRef}
          />
        </Col>
        <Col xs="auto">
          <Form.Label className="form-label mb-0 date-separator">to</Form.Label>
        </Col>
        <Col xs="auto">
          <DateInput.New
            placeholder="mm/dd/yyyy"
            className="date-input shadow-box"
            value={[filters.endDate]}
            onChange={(end) => {
              const val = new Dayts(end).endOf('day');
              const start = filters.startDate ? new Dayts(filters.startDate).startOf('day') : false;
              onFilterChange('endDate', val.format(DaytsFormatEnum.DATE_INPUT));
              if (!start || val.isBefore(start))
                onFilterChange('startDate', new Dayts(val).startOf('day').format(DaytsFormatEnum.DATE_INPUT));
              endDateRef.current?.click();
            }}
            ref={endDateRef}
          />
        </Col>
        <Col xs="auto">
          <Dropdown
            name="type"
            className="shadow-box"
            value={filters.type}
            onChange={(val) => {
              onFilterChange('type', val);
            }}
            items={[
              {
                label: 'Drive Time Search',
                value: 'report',
              },
              {
                label: 'Manifest Import',
                value: 'import',
              },
            ]}
            options={{
              locale: {
                'Select...': 'All Types',
              },
            }}
          />
        </Col>
        <Col xs="auto" className="d-flex align-items-center gap-2">
          <Form.Label className="filter-label mb-0">Pickup:</Form.Label>
          <LocationDropdown
            className="shadow-box"
            value={filters.puLocationId}
            onChange={(value) => onFilterChange('puLocationId', value)}
            placeholder="Filter by pickup location"
          />
        </Col>
        <Col xs="auto" className="d-flex align-items-center gap-2">
          <Form.Label className="filter-label mb-0">Dropoff:</Form.Label>
          <LocationDropdown
            className="shadow-box"
            value={filters.doLocationId}
            onChange={(value) => onFilterChange('doLocationId', value)}
            placeholder="Filter by dropoff location"
          />
        </Col>
        <Col xs="auto" className="d-flex gap-3">
          <Button
            variant="green"
            className="go-button px-4 shadow-box"
            onClick={() => {
              // always clear import id on go search
              onFilterChange('importId', '');
              handleSubmit();
            }}
          >
            GO
          </Button>
          <Button name="resetFilters" className="px-4 shadow-box" variant="gray-subtle" onClick={handleReset}>
            RESET
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default HistoryFilters;
